import React, { useState, useMemo, useEffect } from "react";
import { useData } from "../context/DataContext";
import { confirmDelete, successToast, errorToast } from "../utils/dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faMagnifyingGlass,
  faNoteSticky,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatDateOnly = (s) => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
};

function NotesModal({ enquiry, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!enquiry) return null;
  const text = enquiry.remarks || enquiry.message || "(no notes)";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-[#14251F]">
              Notes from {enquiry.name || "guest"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {formatDate(enquiry.createdAt)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 cursor-pointer p-2 -m-2"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} size="lg" />
          </button>
        </header>
        <div className="p-5">
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {text}
          </p>
        </div>
        <footer className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] rounded-md cursor-pointer font-medium"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function EnquiryList() {
  const { contactFormSubmissions = [], deleteContactFormSubmission } =
    useData();
  const [query, setQuery] = useState("");
  const [activeEnquiry, setActiveEnquiry] = useState(null);

  const sorted = useMemo(() => {
    return [...contactFormSubmissions].sort((a, b) =>
      (b.createdAt || "").localeCompare(a.createdAt || "")
    );
  }, [contactFormSubmissions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((s) =>
      [s.name, s.email, s.phone, s.property, s.remarks, s.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [sorted, query]);

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete enquiry?",
        text: "This enquiry will be permanently removed.",
      })
    ) {
      try {
        await deleteContactFormSubmission(id);
        successToast("Enquiry deleted.");
      } catch (err) {
        console.error(err);
        errorToast("Error deleting enquiry.");
      }
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Enquiries</h2>
          <p className="text-sm text-gray-500">
            {filtered.length} of {sorted.length} contact form submissions
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
          />
          <input
            type="text"
            placeholder="Search name, phone, email, remarks…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D48C1E] focus:border-transparent"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
          {sorted.length === 0
            ? "No enquiries yet."
            : "No enquiries match your search."}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Phone</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Property</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">
                    Check-in → Check-out
                  </th>
                  <th className="px-4 py-3 font-medium text-center">Guests</th>
                  <th className="px-4 py-3 font-medium text-center">Notes</th>
                  <th className="px-4 py-3 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((s) => {
                  const hasNotes = !!(s.remarks || s.message);
                  const stayRange =
                    s.checkin || s.checkout
                      ? `${formatDateOnly(s.checkin) || "—"} → ${
                          formatDateOnly(s.checkout) || "—"
                        }`
                      : "—";
                  return (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDate(s.createdAt)}
                      </td>
                      <td className="px-4 py-3 font-medium text-[#14251F]">
                        {s.name || "—"}
                      </td>
                      <td className="px-4 py-3">
                        {s.phone ? (
                          <a
                            href={`tel:${s.phone}`}
                            className="text-[#D48C1E] hover:underline"
                          >
                            {s.phone}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {s.email ? (
                          <a
                            href={`mailto:${s.email}`}
                            className="text-[#D48C1E] hover:underline break-all"
                          >
                            {s.email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {s.property || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {stayRange}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700">
                        {s.guests || "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hasNotes ? (
                          <button
                            onClick={() => setActiveEnquiry(s)}
                            className="inline-flex items-center gap-1.5 text-[#14251F] hover:text-[#D48C1E] cursor-pointer text-xs font-medium"
                            title="View notes"
                          >
                            <FontAwesomeIcon icon={faNoteSticky} />
                            View
                          </button>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="text-red-500 hover:text-red-700 cursor-pointer p-2 -m-2"
                          title="Delete enquiry"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <NotesModal
        enquiry={activeEnquiry}
        onClose={() => setActiveEnquiry(null)}
      />
    </div>
  );
}
